import { corporateCMSApi } from "~/Services";
import { ModuleConfig, ModuleFields } from "~/Utils/drilldownData";

export const generateFields = (
  basePath: string,
  languages: { code: string; label: string; parseField: string }[]
): ModuleFields[] => {
  return languages.flatMap((lang) => {
    const fields: ModuleFields[] = [];

    // 🖼️ Image — only for English
    if (lang.parseField === "field_content_english") {
      fields.push({
        fieldName: `Image`,
        fieldValue: `${basePath}.banner`,
        parseField: lang.parseField,
        className: "col-span-6",
        type: "image",
        name: `${lang.parseField}.${basePath}.banner`,
        formType: "file",
        usedFileType: "image/*",
        label: `Image`,
        placeholder: `Select Image`,
        validation: { required: `Image is required` },
      });
    }

    // 📝 Title, Intro Text, Extro Text — all languages
    fields.push(
      {
        fieldName: `Title (${lang.code})`,
        fieldValue: `${basePath}.title`,
        parseField: lang.parseField,
        className: "col-span-6",
        type: "content",
        name: `${lang.parseField}.${basePath}.title`,
        formType: "input",
        label: `Title (${lang.code})`,
        placeholder: `Enter Title ${lang.code}`,
        validation:
          lang.parseField === "field_content_english" ? { required: `Title ${lang.code} is required` } : undefined,
      },
      {
        fieldName: `Intro Text (${lang.code})`,
        fieldValue: `${basePath}.introText`,
        parseField: lang.parseField,
        className: "col-span-6",
        type: "content",
        name: `${lang.parseField}.${basePath}.introText`,
        formType: "richText",
        label: `Intro Text (${lang.code})`,
        placeholder: `Enter Intro Text ${lang.code}`,
        validation:
          lang.parseField === "field_content_english" ? { required: `Intro Text ${lang.code} is required` } : undefined,
      },
      {
        fieldName: `Extro Text (${lang.code})`,
        fieldValue: `${basePath}.extroText`,
        parseField: lang.parseField,
        className: "col-span-6",
        type: "content",
        name: `${lang.parseField}.${basePath}.extroText`,
        formType: "richText",
        label: `Extro Text (${lang.code})`,
        placeholder: `Enter Extro Text ${lang.code}`,
        validation:
          lang.parseField === "field_content_english" ? { required: `Extro Text ${lang.code} is required` } : undefined,
      }
    );

    return fields;
  });
};

// 🔹 Helper function for repeated pattern (Side Data & Features)
const generateImageFieldIfEnglish = (basePath: string, lang: any, path: string): ModuleFields[] => {
  if (lang.parseField !== "field_content_english") return [];
  return [
    {
      fieldName: `Image (${lang.code})`,
      fieldValue: path,
      parseField: lang.parseField,
      className: "col-span-6",
      type: "image",
      name: `${lang.parseField}.${path}`,
      formType: "file",
      usedFileType: "image/*",
      label: `Image (${lang.code})`,
      placeholder: `Select Image`,
      validation: { required: `Image is required` },
    },
  ];
};

// 📦 Side Data
export const generateFieldsSideData = (
  basePath: string,
  languages: { code: string; label: string; parseField: string }[],
  idx: number
): ModuleFields[] => {
  return languages.flatMap((lang) => [
    ...generateImageFieldIfEnglish(basePath, lang, `${basePath}.siderData[${idx}].image`),
    {
      fieldName: `Title (${lang.code})`,
      fieldValue: `${basePath}.siderData[${idx}].title`,
      parseField: lang.parseField,
      className: "col-span-6",
      type: "content",
      name: `${lang.parseField}.${basePath}.siderData[${idx}].title`,
      formType: "input",
      label: `Title (${lang.code})`,
      placeholder: `Enter Title ${lang.code}`,
      validation:
        lang.parseField === "field_content_english" ? { required: `Title ${lang.code} is required` } : undefined,
    },
    {
      fieldName: `Description (${lang.code})`,
      fieldValue: `${basePath}.siderData[${idx}].description`,
      parseField: lang.parseField,
      className: "col-span-6",
      type: "content",
      name: `${lang.parseField}.${basePath}.siderData[${idx}].description`,
      formType: "richText",
      label: `Description (${lang.code})`,
      placeholder: `Enter Description ${lang.code}`,
      validation:
        lang.parseField === "field_content_english" ? { required: `Description ${lang.code} is required` } : undefined,
    },
  ]);
};

// 📦 Features
export const generateFieldsFeatures = (
  basePath: string,
  languages: { code: string; label: string; parseField: string }[],
  idx: number
): ModuleFields[] => {
  return languages.flatMap((lang) => [
    ...generateImageFieldIfEnglish(basePath, lang, `${basePath}.features[${idx}].image`),
    {
      fieldName: `Title (${lang.code})`,
      fieldValue: `${basePath}.features[${idx}].title`,
      parseField: lang.parseField,
      className: "col-span-6",
      type: "content",
      name: `${lang.parseField}.${basePath}.features[${idx}].title`,
      formType: "input",
      label: `Title (${lang.code})`,
      placeholder: `Enter Title ${lang.code}`,
      validation:
        lang.parseField === "field_content_english" ? { required: `Title ${lang.code} is required` } : undefined,
    },
    {
      fieldName: `Description (${lang.code})`,
      fieldValue: `${basePath}.features[${idx}].description`,
      parseField: lang.parseField,
      className: "col-span-6",
      type: "content",
      name: `${lang.parseField}.${basePath}.features[${idx}].description`,
      formType: "richText",
      label: `Description (${lang.code})`,
      placeholder: `Enter Description ${lang.code}`,
      validation:
        lang.parseField === "field_content_english" ? { required: `Description ${lang.code} is required` } : undefined,
    },
    {
      fieldName: `Color (${lang.code})`,
      fieldValue: `${basePath}.features[${idx}].color`,
      parseField: lang.parseField,
      className: "col-span-6",
      type: "content",
      name: `${lang.parseField}.${basePath}.features[${idx}].color`,
      formType: "color",
      label: `Color (${lang.code})`,
      placeholder: `Enter Color ${lang.code}`,
      validation:
        lang.parseField === "field_content_english" ? { required: `Color ${lang.code} is required` } : undefined,
    },
  ]);
};

// 🌍 Languages
export const languages = [
  { code: "EN", label: "English", parseField: "field_content_english" },
  { code: "MS", label: "Malay", parseField: "field_content_malay" },
  { code: "ZH", label: "Mandarin", parseField: "field_content_mandarin" },
];

let url = `/api/page-content/all-fields?pageName=sustainability/community-friendly-organisation`;

export let getData: { [key in ModuleConfig]: ModuleFields[] } | null = null;

const fetchProfileData = async (): Promise<{ [key in ModuleConfig]: ModuleFields[] } | null> => {
  try {
    const res = await corporateCMSApi.get(url);
    const profileData = res.data;

    if (!profileData?.[0]?.field_content_english?.data) {
      return null;
    }

    const dataFields = generateFields("data", languages);

    const dataFieldsSideData =
      profileData?.[0]?.field_content_english?.data?.siderData?.map((_: any, i: number) =>
        generateFieldsSideData("data", languages, i)
      ) || [];

    const dataFieldsFeatures =
      profileData?.[0]?.field_content_english?.data?.features?.map((_: any, i: number) =>
        generateFieldsFeatures("data", languages, i)
      ) || [];

    return {
      communityFriendlyOrganisationMainContent: dataFields,
      communityFriendlyOrganisationSiderData: dataFieldsSideData.flat(),
      communityFriendlyOrganisationFeatures: dataFieldsFeatures.flat(),
    };
  } catch (error) {
    console.error("Error fetching profile data:", error);
    return null;
  }
};

export const getCommunityFriendlyOrganisationDrillDownData = async () => {
  if (!getData) {
    getData = await fetchProfileData();
  }
  return getData;
};
